'use client';

import { type ComponentProps, type ReactElement } from 'react';
import type { SupplyChainContent } from '@/lib/api/fetch-supply-chain';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { SvgRight } from '../icons-eataly';

export type SupplyChainProps = {
    /** Supply chain custom object content from the category route loader */
    data: SupplyChainContent | null;
    loading?: 'lazy' | 'eager';
} & ComponentProps<'section'>;

/** PLP supply chain storytelling block — data comes from the category route loader. */
export default function SupplyChain({
    data,
    loading = 'lazy',
    className,
    ...props
}: SupplyChainProps): ReactElement | null {
    const { t } = useTranslation('supplyChain');
    const { c_pimSupplyChainDescription, c_image, c_pimSupplyChainName, c_bgColor } = data ?? {};

    const hasBody = Boolean(c_pimSupplyChainDescription?.trim());
    const hasTitleText = Boolean(c_pimSupplyChainName?.trim());
    const imageSrc = c_image ? c_image : undefined;
    const hasImage = Boolean(imageSrc);
    const proseClassName =
        'text-neutral-10 body-copy-l [&_strong]:font-bold line-clamp-6 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-1 [&_a]:decoration-1 [&_a]:decoration-primary/50 [&_a]:transition-all [&_a]:duration-150 [&_a]:ease-in-out [&_a]:hover:text-primary/80 [&_a]:hover:decoration-primary/80 [&_ul]:list-disc [&_ul]:list-inside [&_ol]:list-decimal [&_ol]:list-inside';

    // Omit the block when the custom object exists but carries no editorial payload (no orphan chrome).
    if (!hasBody && !hasTitleText && !hasImage) {
        return null;
    }

    return (
        <section className={className} data-component="supply-chain" {...props}>
            <div
                className={cn(
                    'py-8 lg:py-20 grid-global gap-y-6 lg:gap-y-0',
                    c_bgColor ? `${c_bgColor}` : 'bg-neutral-white'
                )}>
                <div
                    className={cn(
                        'flex flex-col gap-6 justify-center col-span-6',
                        hasImage ? ' lg:col-start-2 lg:col-span-6 lg:pe-20' : 'lg:col-start-4 lg:col-end-10'
                    )}>
                    <div className="flex flex-col gap-2">
                        <p className={cn('label-l text-muted-foreground', !hasImage && 'text-center')}>{t('title')}</p>
                        {hasTitleText && (
                            <h2 className={cn(!hasImage && 'mx-auto', 'heading-headline-m text-neutral-10')}>
                                {c_pimSupplyChainName}
                            </h2>
                        )}
                    </div>
                    {hasBody && c_pimSupplyChainDescription && (
                        <div
                            className={proseClassName}
                            // eslint-disable-next-line react/no-danger -- BM/PIM-authored HTML
                            dangerouslySetInnerHTML={{ __html: c_pimSupplyChainDescription }}
                        />
                    )}
                    <Button
                        asChild
                        hierarchy="secondary"
                        iconAfter={<SvgRight />}
                        className={cn(!hasImage && 'mx-auto', 'border-none w-fit p-0 cursor-default')}>
                        <span aria-disabled="true">{t('discoverProject')}</span>
                    </Button>
                </div>

                {hasImage && imageSrc && (
                    <div className="relative rounded-lg col-span-6 lg:col-start-8 lg:col-span-4 w-full h-[418px] lg:h-[630px] 2xl:max-h-[900px] aspect-[1/2] lg:aspect-auto overflow-hidden">
                        <DynamicImage
                            src={imageSrc}
                            alt={c_pimSupplyChainName ?? ''}
                            className="h-full w-full"
                            imageProps={{ className: 'block h-full w-full object-cover' }}
                            widths={{ base: '100vw', lg: '33vw' }}
                            loading={loading}
                            priority={loading === 'eager' ? 'high' : undefined}
                        />
                    </div>
                )}
            </div>
        </section>
    );
}
